import api from '@/utils/request.js'
import { API } from '@/service/apiConfig'
import { deepClone } from '@/utils/util'

const LOCAL_STORAGE_KEY = 'record-time-list'

// 上报时间
async function reportTime({ url = API.questionnaireTimeOnPage, reportTimeData }) {
  if (!reportTimeData.params.duration) return
  try {
    const res = await api.get(url, {
      timeout: 4000,
      params: reportTimeData.params
    })
    return res
  } catch (error) {
    console.warn('Report time failed:', error)
    throw error
  }
}

// 处理在线状态下的数据上报
async function handleOnline() {
  const waitReportTime = getWaitReportTimeToLocal()

  if (Object.keys(waitReportTime).length === 0) return

  // 添加最大批次限制
  const MAX_BATCH_SIZE = 5

  for (const [key, records] of Object.entries(waitReportTime)) {
    if (!Array.isArray(records) || records.length === 0) continue

    // 合并具有相同业务标识和业务类型的记录
    const mergedRecords = mergeRecordsByBusiness(records)

    // 分批处理请求
    for (let i = 0; i < mergedRecords.length; i += MAX_BATCH_SIZE) {
      const batch = mergedRecords.slice(i, i + MAX_BATCH_SIZE)
      try {
        await Promise.all(batch.map(record => reportTime({ reportTimeData: record })))
        // 成功上报的记录从原数组中移除
        records.splice(i, batch.length)
      } catch (error) {
        console.error('Error reporting time batch:', error)
        // 发生错误时停止处理当前 key 的剩余记录
        break
      }
    }

    // 如果还有未处理的记录，更新存储
    if (records.length > 0) {
      waitReportTime[key] = records
    } else {
      delete waitReportTime[key]
    }
  }

  // 更新本地存储
  if (Object.keys(waitReportTime).length > 0) {
    window.localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(waitReportTime))
  } else {
    clearLocalReportTime()
  }
}

// 合并具有相同业务标识和业务类型的记录
function mergeRecordsByBusiness(records) {
  const mergedMap = new Map()

  records.forEach(record => {
    const { businessUniqueId, businessRecordType, duration, ...restParams } = record.params
    const uniqueKey = `${businessUniqueId}-${businessRecordType}`

    if (mergedMap.has(uniqueKey)) {
      // 如果已存在相同业务标识和类型的记录，累加 duration
      mergedMap.get(uniqueKey).params.duration += duration
    } else {
      // 否则，新增一条记录
      mergedMap.set(uniqueKey, {
        ...record,
        params: {
          businessUniqueId,
          businessRecordType,
          duration,
          ...restParams
        }
      })
    }
  })

  return Array.from(mergedMap.values())
}

// 从本地存储获取待上报数据
function getWaitReportTimeToLocal() {
  try {
    return JSON.parse(window.localStorage.getItem(LOCAL_STORAGE_KEY) || '{}')
  } catch (error) {
    console.error('Failed to parse local storage data:', error)
    return {}
  }
}

// 清除本地存储中的上报数据
function clearLocalReportTime() {
  window.localStorage.removeItem(LOCAL_STORAGE_KEY)
}

// 定时器管理类
class TimerManager {
  constructor() {
    this.timers = new Map()
  }

  addTimer(name, func, interval) {
    this.clearTimer(name)
    const timerId = setInterval(() => {
      try {
        func()
      } catch (error) {
        console.error(`Error executing timer "${name}":`, error)
        this.clearTimer(name)
      }
    }, interval)
    this.timers.set(name, {
      id: timerId,
      func: func
    })
  }

  clearTimer(name) {
    if (this.timers.has(name)) {
      clearInterval(this.timers.get(name).id)
      this.timers.delete(name)
    }
  }

  clearAll() {
    for (const name of this.timers.keys()) {
      this.clearTimer(name)
    }
  }
}

// 轮询记录时间类
class PollingRecordTime {
  timer = null;
  pollOpt = {
    localduration: 0,
    timeDifference: 0
  };

  constructor(opt) {
    this.defaultOpt = opt
    this.timer = new TimerManager()
  }

  startTime() {
    this.pollOpt.startTime = performance.now()
    this.pollOpt.prevTime = performance.now()

    // 添加节流，避免频繁调用 handleOnline
    const throttledHandleOnline = this.throttle(handleOnline, 30000)
    throttledHandleOnline()

    this.timer.addTimer(this.defaultOpt.id, async() => {
      const nowTime = performance.now()
      const timeDifference = nowTime - this.pollOpt.prevTime

      if (timeDifference > this.defaultOpt.isJumpTimeMinInterval) {
        this.pollOpt.localduration = this.pollOpt.prevTime - this.pollOpt.startTime
        this.pollOpt.startTime = nowTime
      }
      this.pollOpt.prevTime = nowTime
      this.defaultOpt.timerCallBack && this.defaultOpt.timerCallBack()

      this.pollOpt.timeDifference = nowTime - this.pollOpt.startTime + this.pollOpt.localduration

      if (timeDifference <= this.defaultOpt.isJumpTimeMinInterval &&
          (this.pollOpt.timeDifference / 1000).toFixed() % (this.defaultOpt.interval / 1000).toFixed() == 0) {
        const reportTimeData = this.formatReportParams()

        // 如果不在线或页面隐藏，直接保存到本地
        if (!navigator.onLine || document.hidden) {
          this.saveTimeToLocal(reportTimeData)
        } else {
          try {
            await reportTime({ url: this.defaultOpt.url, reportTimeData })
          } catch (error) {
            // 上报失败时保存到本地
            this.saveTimeToLocal(reportTimeData)
          }
        }

        this.pollOpt.localduration = 0
        this.pollOpt.startTime = performance.now()
      }
    }, 1000)
  }

  formatReportParams() {
    const params = {
      endTime: performance.now(),
      params: {
        ...this.defaultOpt.params,
        businessUniqueId: this.defaultOpt.id,
        duration: Number((this.pollOpt.timeDifference / 1000).toFixed()),
        t: performance.now()
      },
      ...this.pollOpt
    }
    return params
  }

  async interrupt(isReport = true) {
    console.log('[PollingRecordTime.interrupt] Called with isReport:', isReport)
    this.timer?.clearTimer(this.defaultOpt.id)

    if (!isReport) return

    const params = this.formatReportParams()
    // 如果不在线或页面隐藏，直接保存到本地
    if (!navigator.onLine || document.hidden) {
      this.saveTimeToLocal(params)
    } else {
      try {
        await reportTime({ url: this.defaultOpt.url, reportTimeData: params })
      } catch (error) {
        this.saveTimeToLocal(params)
      }
    }
  }

  start() {
    this.startTime()
  }

  async pause() {
    await this.interrupt()
  }

  resume() {
    this.startTime()
  }

  async close(isReport = true) {
    await this.interrupt(isReport)
  }

  saveTimeToLocal(reportTimeData) {
    const obj = getWaitReportTimeToLocal()
    const { businessUniqueId, businessRecordType } = reportTimeData.params
    const uniqueKey = `${businessUniqueId}-${businessRecordType}`

    if (!Array.isArray(obj[uniqueKey])) {
      obj[uniqueKey] = []
    }

    // 检查是否存在相同业务标识和类型的记录
    const existingRecordIndex = obj[uniqueKey].findIndex(
      record =>
        record.params.businessUniqueId === businessUniqueId &&
        record.params.businessRecordType === businessRecordType
    )

    if (existingRecordIndex !== -1) {
      // 如果存在，累加 duration
      obj[uniqueKey][existingRecordIndex].params.duration += reportTimeData.params.duration
    } else {
      // 否则，新增一条记录
      obj[uniqueKey].push(reportTimeData)
    }

    window.localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(obj))
  }

  // 添加节流函数
  throttle(func, wait) {
    let lastTime = 0
    return function() {
      const now = Date.now()
      if (now - lastTime >= wait) {
        func.apply(this, arguments)
        lastTime = now
      }
    }
  }
}

// IM 记录时间类（暂未实现）
class ImRecordTime { }

/**
 * 记录时间主类
 * @class RecordTime
 * @param {Object} opt - 配置选项
 * @param {string} opt.id - 业务唯一标识符（必填）
 * @param {string} [opt.useType='Polling'] - 记录时间的方式：'Polling'（轮询）或 'Im'（即时）
 * @param {number} [opt.interval=10000] - 轮询间隔时间（毫秒）
 * @param {string} [opt.url=API.questionnaireTimeOnPage] - 上报接口地址
 * @param {string} [opt.version='0.0.1'] - 版本号
 * @param {number} [opt.isJumpTimeMinInterval=5000] - 判定为时间跳变的最小间隔（毫秒）
 * @param {string} [opt.lifeCycle='beforeStart'] - 生命周期状态：'beforeStart'、'start'、'pause'、'close'
 * @param {Object} [opt.params] - 上报时的附加参数
 * @param {number} [opt.params.timerType] - 计时器类型：0（轮询）或 1（即时）
 * @param {Function} [opt.timerCallBack] - 每次计时回调函数
 * @throws {Error} 当未提供 id 时抛出错误
 *
 * @example
 * const recordTime = new RecordTime({
 *   id: 'business-123',
 *   useType: 'Polling',
 *   interval: 5000,
 *   params: {
 *     businessRecordType: 2
 *   }
 * });
 */
export class RecordTime {
  copyDefaultOpt = {};

  constructor(opt) {
    if (!opt.id) {
      return console.error('RecordTime: id is a required field. Please provide a valid id parameter.', opt)
    }
    this.defaultOpt = {
      useType: 'Polling',
      interval: 10000,
      url: API.questionnaireTimeOnPage,
      version: '0.0.1',
      isJumpTimeMinInterval: 5000,
      lifeCycle: 'beforeStart',
      ...opt
    }
    this.defaultOpt = this.formatParams(this.defaultOpt)
    this.copyDefaultOpt = deepClone(this.defaultOpt)
    this.init(this.defaultOpt)
  }

  init(defaultOpt) {
    if (defaultOpt.useType === 'Polling') {
      this._recordTime = new PollingRecordTime(defaultOpt)
    } else if (this.defaultOpt.useType === 'Im') {
      this._recordTime = new ImRecordTime(defaultOpt)
    } else {
      return console.warn('useType 不合法')
    }
  }

  start() {
    if (['beforeStart', 'pause'].includes(this.defaultOpt.lifeCycle)) {
      this.defaultOpt.lifeCycle = 'start'
      this._recordTime.start()
    }
  }

  close(isReport) {
    if (['pause', 'start'].includes(this.defaultOpt.lifeCycle)) {
      this.defaultOpt.lifeCycle = 'close'
      this._recordTime.close(isReport)
      this.init(this.copyDefaultOpt)
    }
  }

  pause() {
    if (this.defaultOpt.lifeCycle === 'start') {
      this.defaultOpt.lifeCycle = 'pause'
      this._recordTime.pause()
    }
  }

  resume() {
    if (this.defaultOpt.lifeCycle === 'pause') {
      this.defaultOpt.lifeCycle = 'start'
      this._recordTime.resume()
    }
  }

  async getDurationTime() {
    const { data } = await api.get(API.getDurationTime, {
      params: {
        businessUniqueId: this.defaultOpt.id,
        businessRecordType: this.defaultOpt.params.businessRecordType
      }
    })
    return data
  }

  formatParams(reportParams) {
    reportParams.params = reportParams.params || {}
    if (reportParams.useType === 'Polling') {
      reportParams.params['timerType'] = 0
    } else if (reportParams.useType === 'Im') {
      reportParams.params['timerType'] = 1
    }
    return reportParams
  }
}

// 创建网络事件监听器
export function createNetworkEventListener() {
  let onlineListenerAdded = false

  const addListeners = () => {
    if (onlineListenerAdded) return
    window.addEventListener('online', online)
    window.addEventListener('offline', offline)
    onlineListenerAdded = true
  }

  const removeListeners = () => {
    if (!onlineListenerAdded) return
    window.removeEventListener('online', online)
    window.removeEventListener('offline', offline)
    onlineListenerAdded = false
  }

  const online = () => {
    handleOnline()
  }

  const offline = () => {
    console.log('网络已断开', navigator.onLine)
  }

  return {
    add: addListeners,
    remove: removeListeners
  }
}

// 记录时间混入
export const recordTimeMixin = {
  data() {
    return {
      record__key: {},
      record__timmer: {}
    }
  },
  mounted() {
    this.addRecordEventListener()
  },
  methods: {
    addRecordEventListener() {
      document.removeEventListener('visibilitychange', this.visibilitychange)
      if (typeof document.addEventListener === 'function') {
        document.addEventListener('visibilitychange', this.visibilitychange)
      }
    },
    async visibilitychange() {
      if (document.hidden) {
        await this.batchProcessing('pause')
      } else {
        await this.batchProcessing('resume')
      }
    },
    async batchProcessing(funName) {
      try {
        for (const key in this.record__key) {
          await this.record__key[key][funName]()
        }
      } catch (error) {
        console.log('%c [error]', 'font-size:15px; color:#42b983;', error)
      }
    }
  },
  async beforeDestroy() {
    document.removeEventListener('visibilitychange', this.visibilitychange)
    clearInterval(this.record__timmer?.cumulativeSeconds)
    await this.batchProcessing('close')
  }
}
